import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { BookmarkIcon, CubeIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkIconSolid } from '@heroicons/react/24/solid';

import { Badge } from '@oysterjs/ui/badge';

import { type CustomPresetFieldsFragment } from '~/api/graphql';
import { ProLabel } from '~/components/pro-label';
import { sharedAccessBadgeLabel } from '~/components/presets/presets.utils';
import { USE_CASE_CATEGORIES, familyForPreset, type UseCaseSpec } from './use-case-catalog';

const TILE_CLASS = clsx(
  'flex min-h-32 w-full flex-col gap-2.5 rounded-xl shadow bg-white dark:bg-neutral-800 p-4 text-left',
  'shadow-sm transition-all duration-300 hover:shadow-xl',
  'disabled:pointer-events-none disabled:cursor-not-allowed disabled:hover:shadow-sm'
);

const CUSTOM_PRESET_CHIP =
  'bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300';

export const UseCaseChip = ({
  useCase,
  className
}: {
  useCase: UseCaseSpec;
  className?: string;
}) => {
  const Icon = useCase.icon;
  return (
    <span
      className={clsx(
        'inline-flex rounded p-2',
        USE_CASE_CATEGORIES[useCase.category].chip,
        className
      )}
    >
      <Icon className="size-4" />
    </span>
  );
};

export const UseCaseTile = ({
  useCase,
  disabled,
  disabledReason = 'requires the Pro plan',
  onClick
}: {
  useCase: UseCaseSpec;
  disabled?: boolean;
  /** Why the tile is inert, read out as part of its label. */
  disabledReason?: string;
  onClick: () => void;
}) => (
  <Headless.Button
    type="button"
    aria-label={disabled ? `${useCase.title} (${disabledReason})` : useCase.title}
    title={useCase.title}
    disabled={disabled}
    onClick={onClick}
    className={TILE_CLASS}
  >
    <div className="flex items-center justify-between gap-2">
      <UseCaseChip useCase={useCase} className="group-disabled:opacity-50" />
      {disabled && disabledReason === 'requires the Pro plan' && (
        <ProLabel className="pointer-events-auto" />
      )}
    </div>
    <div className="text-sm font-semibold leading-snug text-neutral-900 dark:text-white group-disabled:opacity-50">
      {useCase.title}
    </div>
    <div className="text-pretty text-xs leading-relaxed text-neutral-500 dark:text-neutral-400 group-disabled:opacity-50">
      {useCase.description}
    </div>
  </Headless.Button>
);

// `h-full` plus `mt-auto` on the badge row keeps a grid row one height.
const CARD_CLASS = clsx(
  'group relative flex h-full flex-col gap-2 rounded-xl border bg-white dark:bg-neutral-800 p-3',
  'transition-shadow focus-within:shadow-md'
);

/** Full-card hit target, under the pin/info buttons so they stay their own controls. */
const CardLaunchButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Headless.Button
    type="button"
    aria-label={label}
    onClick={onClick}
    className="absolute inset-0 rounded-xl focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
  />
);

const CardOverlayButton = ({
  label,
  pressed,
  disabled,
  onClick,
  className,
  children
}: {
  label: string;
  pressed?: boolean;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}) => (
  <Headless.Button
    type="button"
    aria-label={label}
    aria-pressed={pressed}
    disabled={disabled}
    onClick={onClick}
    className={clsx(
      'pointer-events-auto relative rounded p-1 transition-colors',
      'disabled:opacity-50',
      className
    )}
  >
    {children}
  </Headless.Button>
);

/** Locked cards stay clickable so the Pro dialog can explain the gate. */
export const UseCaseCard = ({
  useCase,
  pinned = false,
  locked,
  onRun,
  onTogglePin,
  onExplain
}: {
  useCase: UseCaseSpec;
  pinned?: boolean;
  locked?: boolean;
  onRun?: () => void;
  /** Omitted where the card is picked rather than pinned, e.g. a trigger's preset. */
  onTogglePin?: () => void;
  onExplain: () => void;
}) => {
  const family = familyForPreset(useCase.preset);
  const PinIcon = pinned ? BookmarkIconSolid : BookmarkIcon;

  return (
    <div
      className={clsx(
        CARD_CLASS,
        pinned
          ? 'border-primary-200 dark:border-primary-500/30'
          : 'border-neutral-200 dark:border-neutral-700',
        onRun && 'hover:shadow-md'
      )}
    >
      {onRun && (
        <CardLaunchButton
          label={locked ? `${useCase.title} (requires the Pro plan)` : useCase.title}
          onClick={onRun}
        />
      )}

      {/* Inert as a layer, so the launch button underneath keeps the whole card. With
          no launch button there is nothing to fall through to, so the text behaves
          like text. */}
      <div
        className={clsx('flex items-start justify-between gap-2', onRun && 'pointer-events-none')}
      >
        <UseCaseChip useCase={useCase} className={clsx(locked && 'opacity-50')} />
        <div className="flex items-center">
          {useCase.pro && <ProLabel className="pointer-events-auto relative" />}
          <CardOverlayButton
            label={`What ${useCase.title} does`}
            onClick={onExplain}
            className="text-neutral-300 dark:text-neutral-600 hover:text-neutral-500 dark:hover:text-neutral-400"
          >
            <InformationCircleIcon className="size-4" />
          </CardOverlayButton>
          {onTogglePin && (
            <CardOverlayButton
              label={pinned ? `Unpin ${useCase.title}` : `Pin ${useCase.title}`}
              pressed={pinned}
              disabled={locked}
              onClick={onTogglePin}
              className={clsx(
                pinned
                  ? 'text-primary-500 dark:text-primary-400 hover:text-primary-600 dark:hover:text-primary-400'
                  : 'text-neutral-300 dark:text-neutral-600 hover:text-neutral-500 dark:hover:text-neutral-400'
              )}
            >
              <PinIcon className="size-4" />
            </CardOverlayButton>
          )}
        </div>
      </div>

      <div className={clsx('flex flex-col gap-1.5', onRun && 'pointer-events-none')}>
        <span
          className={clsx(
            'text-sm font-semibold leading-snug',
            locked ? 'text-neutral-400 dark:text-neutral-500' : 'text-neutral-900 dark:text-white'
          )}
        >
          {useCase.title}
        </span>
        <span className="text-pretty text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
          {useCase.description}
        </span>
      </div>

      <div
        className={clsx(
          'mt-auto flex flex-wrap items-center gap-1.5 pt-1',
          onRun && 'pointer-events-none'
        )}
      >
        {family && (
          <Badge color="zinc" className="max-w-full truncate">
            {family.title}
          </Badge>
        )}
      </div>
    </div>
  );
};

export const CustomPresetChip = ({ className }: { className?: string }) => (
  <span className={clsx('inline-flex rounded p-2', CUSTOM_PRESET_CHIP, className)}>
    <CubeIcon className="size-4" />
  </span>
);

const CustomPresetBadges = ({ preset }: { preset: CustomPresetFieldsFragment }) => {
  const network = sharedAccessBadgeLabel(preset);
  return (
    <>
      <Badge color="purple">Custom</Badge>
      {network && <Badge color="sky">{network}</Badge>}
    </>
  );
};

export const CustomPresetTile = ({
  preset,
  disabled,
  onClick
}: {
  preset: CustomPresetFieldsFragment;
  disabled?: boolean;
  onClick: () => void;
}) => (
  <Headless.Button
    type="button"
    aria-label={`${preset.title} (custom preset)`}
    title={preset.title}
    onClick={onClick}
    disabled={disabled}
    className={TILE_CLASS}
  >
    <div className="flex items-center justify-between gap-2">
      <CustomPresetChip />
      <Badge color="purple">Custom</Badge>
    </div>
    <div className="text-sm font-semibold leading-snug text-neutral-900 dark:text-white group-disabled:opacity-50">
      {preset.title}
    </div>
    <div className="text-pretty text-xs leading-relaxed text-neutral-500 dark:text-neutral-400 group-disabled:opacity-50">
      {preset.description}
    </div>
  </Headless.Button>
);

export const CustomPresetCard = ({
  preset,
  pinned = false,
  onRun,
  onTogglePin,
  onExplain
}: {
  preset: CustomPresetFieldsFragment;
  pinned?: boolean;
  /** Omitted where there is nothing to run, e.g. an admin arranging someone else's home screen. */
  onRun?: () => void;
  onTogglePin?: () => void;
  onExplain: () => void;
}) => {
  const PinIcon = pinned ? BookmarkIconSolid : BookmarkIcon;

  return (
    <div
      className={clsx(
        CARD_CLASS,
        onRun && 'hover:shadow-md',
        pinned
          ? 'border-primary-200 dark:border-primary-500/30'
          : 'border-neutral-200 dark:border-neutral-700'
      )}
    >
      {onRun && <CardLaunchButton label={`${preset.title} (custom preset)`} onClick={onRun} />}

      <div
        className={clsx('flex items-start justify-between gap-2', onRun && 'pointer-events-none')}
      >
        <CustomPresetChip />
        <div className="flex items-center">
          <CardOverlayButton
            label={`What ${preset.title} does`}
            onClick={onExplain}
            className="text-neutral-300 dark:text-neutral-600 hover:text-neutral-500 dark:hover:text-neutral-400"
          >
            <InformationCircleIcon className="size-4" />
          </CardOverlayButton>
          {onTogglePin && (
            <CardOverlayButton
              label={pinned ? `Unpin ${preset.title}` : `Pin ${preset.title}`}
              pressed={pinned}
              onClick={onTogglePin}
              className={clsx(
                pinned
                  ? 'text-primary-500 dark:text-primary-400 hover:text-primary-600 dark:hover:text-primary-400'
                  : 'text-neutral-300 dark:text-neutral-600 hover:text-neutral-500 dark:hover:text-neutral-400'
              )}
            >
              <PinIcon className="size-4" />
            </CardOverlayButton>
          )}
        </div>
      </div>

      <div className="pointer-events-none flex flex-col gap-1.5">
        <span className="text-sm font-semibold leading-snug text-neutral-900 dark:text-white">
          {preset.title}
        </span>
        <span className="text-pretty text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
          {preset.description}
        </span>
      </div>

      <div className="pointer-events-none mt-auto flex flex-wrap items-center gap-1.5 pt-1">
        <CustomPresetBadges preset={preset} />
      </div>
    </div>
  );
};
